<script lang="ts">
  import {
    Card,
    Title,
    List,
    Button,
    Icon,
    Alert,
    Screenshots,
    MatchRelevanceInfo
  } from '$shared/components';
  import VerifiableLogo from '$shared/images/verifiable-logo.svg';
  import MatchFoundBanner from './MatchFoundBanner.svelte';
  import { AlertOption } from '$shared/models/AlertOption';
  import { onMount } from 'svelte';

  interface Props {
    result?: any;
    reviewProcessing?: boolean;
    reviewDisabled?: boolean;
    isRemoved?: boolean;
    hasPad?: boolean;
    readonly?: boolean;
    onundo?: () => void;
    onapprove?: () => void;
    onreject?: () => void;
    onevent?: (payload: any) => void;
    onpreview?: (url: string) => void;
  }

  let {
    result = {},
    reviewProcessing = false,
    reviewDisabled = false,
    isRemoved = false,
    hasPad = false,
    readonly = false,
    onundo,
    onapprove,
    onreject,
    onevent,
    onpreview
  }: Props = $props();

  let expanded = $state(false);
  let meta: ObjectType<any> = $state({});

  let expandButtonLabel = $derived(expanded ? 'Less' : 'More');
  let expandButtonIcon = $derived(expanded ? 'icon-chevron-up' : 'icon-chevron-down');

  onMount(() => {
    meta = result?.__meta || {};
  });

  const onClickMore = (): void => {
    expanded = !expanded;
  };

  const onClickUndo = () => {
    if (reviewProcessing) return;
    onundo?.();
  };
</script>

<div id={meta?.id || 'sanction-result'} class="sanction-result">
  {#if isRemoved && meta?.verifiedOn}
    <div class="verified" class:hasPad>
      <div class="icon">
        <img src={VerifiableLogo} alt="verifable" />
      </div>
      <div class="text">Verified: {meta.verifiedOn}</div>
    </div>
  {/if}
  {#if meta?.isReview}
    <MatchFoundBanner
      disabled={readonly || reviewDisabled}
      processing={reviewProcessing}
      {onapprove}
      {onreject}
      {onevent}
    />
  {/if}
  <Card>
    {#if meta.matchRelevance}
      <div class="match-relevance">
        <div data-cy="relevance-k" class="label">Match Relevance:</div>
        <div data-cy="relevance-v" class="relevance-value">
          <MatchRelevanceInfo matchRelevance={meta.matchRelevance} />
        </div>
      </div>
    {/if}
    {#if isRemoved}
      <div class="alert">
        <Alert
          data={'This match is out of date and does not represent the most current primary source results'}
          options={AlertOption.warnAlert()}
          maxWidth
        />
      </div>
    {/if}
    <div class="header">
      {#if meta?.name}
        <div class="title">
          <Title type="h3">{meta.name}</Title>
        </div>
      {/if}
      {#if meta?.isAccepted}
        <div data-cy="undo-label" class="undo-label">
          Match accepted {#if !readonly}
            (<span
              class="undo-action"
              class:undo-disabled={reviewProcessing}
              data-cy="undo-btn"
              onclick={onClickUndo}>Undo</span
            >){/if}
        </div>
      {/if}
    </div>
    <div class="result" class:expanded>
      <div class="list">
        <List list={result} />
      </div>
      {#if Array.isArray(meta?.screenshots) && meta.screenshots.length !== 0}
        <div class="screenshot">
          <Screenshots list={meta.screenshots} {onpreview} />
        </div>
      {/if}
    </div>
    <div class="more-button">
      <Button maxWidth onclick={onClickMore}>
        <div class="button-label">
          <div class="text">{expandButtonLabel}</div>
          <div class="icon"><Icon value={expandButtonIcon} /></div>
        </div>
      </Button>
    </div>
  </Card>
</div>

<style>
  .verified {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
  }
  .verified.hasPad {
    padding-top: 24px;
  }
  .verified .icon {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }
  .verified .icon img {
    width: 100%;
  }
  .verified .text {
    display: flex;
    font-weight: normal;
    font-size: 11px;
    line-height: 18px;
    color: var(--grey-medium);
  }
  .alert {
    margin-bottom: 16px;
  }
  .sanction-result {
    margin-bottom: 24px;
  }
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }
  .sanction-result .result {
    max-height: 300px;
    overflow: hidden;
  }
  .result {
    display: flex;
  }
  .result .list {
    flex: 1;
  }
  .undo-label {
    font-size: 12px;
    color: var(--grey-dark);
  }
  .undo-action {
    color: var(--blue);
    cursor: pointer;
  }
  .undo-action.undo-disabled {
    cursor: not-allowed;
  }
  .result .screenshot {
    width: 300px;
    margin-left: 24px;
  }
  .result.expanded {
    max-height: 100%;
  }
  .more-button {
    margin-top: 16px;
  }
  .button-label {
    display: flex;
    align-items: center;
  }
  .button-label .icon {
    display: flex;
    align-items: center;
    margin-left: 8px;
  }
  .match-relevance {
    display: flex;
    padding-bottom: 16px;
  }
  .match-relevance .label {
    color: var(--grey-dark);
  }
  .relevance-value {
    margin-left: 10px;
  }
</style>
